import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql, withPrefix } from "gatsby";
import {
  normalizeJoinPaths,
  normalizePathWithQuery,
  normalizeRelativePath,
} from "../utility/paths";

const getOgImage = (ogImage) => {
  const ogImageData =
    typeof ogImage === "string" ? { src: ogImage } : ogImage && ogImage.src ? ogImage : null;
  if (ogImageData) {
    const { src, width, height } = ogImageData;
    return [
      {
        property: `og:image`,
        content: src,
      },
    ].concat(
      width && height
        ? [
            {
              property: "og:image:width",
              content: width,
            },
            {
              property: "og:image:height",
              content: height,
            },
          ]
        : []
    );
  }
  return [];
};

const ROBOTS_NOINDEX = process.env.GATSBY_ROBOTS_NOINDEX;

const Seo = ({
  description,
  keywords,
  title,
  ogImage,
  path,
  robots,
  seo,
  excludeFromSitemap,
  noIndexNoFollow,
  hrefLanguages,
  generatedLocale,
  children,
}) => {
  const { site, config, footerMenuFiltered } = useStaticQuery(
    graphql`
      query DefaultSEOQuery {
        site {
          siteMetadata {
            title
            siteUrl
            description
            author
            lang
          }
        }
        config: liferayGeneralConfiguration {
          robotsNoIndex
        }
        footerMenuFiltered {
          localeContent {
            ... on LiferayPageExternalLink {
              externalLink
            }
          }
        }
      }
    `
  );

  if (noIndexNoFollow) {
    switch (noIndexNoFollow[0]) {
      case "NoIndex - NoFollow":
        robots = "none";
        break;
      case "NoIndex":
        robots = "noindex";
        break;
      case "NoFollow":
        robots = "nofollow";
        break;
      default:
        break;
    }
  }

  if (
    excludeFromSitemap === "yes" &&
    (!noIndexNoFollow || (noIndexNoFollow && noIndexNoFollow.length === 0))
  ) {
    robots = "none";
  }

  if (ROBOTS_NOINDEX === "true" || !config.robotsNoIndex || config.robotsNoIndex === "[]") {
    robots = "noindex";
  }

  const lang = site.siteMetadata.lang;
  const metaTitle = seo?.titleSeo || title;
  const htmlTitle = `${metaTitle} | ${site.siteMetadata.title}`;
  const ogTitle = `${seo?.titleOg || metaTitle} | ${site.siteMetadata.title}`;
  const metaDescription = seo?.descriptionSeo || description || "";
  const ogDescription = seo?.descriptionOg || metaDescription || description;
  const ogType = seo?.typeOg;
  const ogUrl = seo?.urlOg;
  const metaKeywords = keywords.length > 0 ? keywords.join(", ") : seo?.keywordSeo;
  const hrefLangs = hrefLanguages || seo?.fields?.hrefLanguages;

  const defaultLocale = useMemo(() => {
    return footerMenuFiltered?.localeContent?.externalLink;
  }, [footerMenuFiltered]);

  return (
    <>
      <html lang={lang} />
      <title>{htmlTitle}</title>
      {metaDescription && <meta name="description" content={metaDescription} />}
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta name="format-detection" content="telephone=no" />
      <meta name="format-detection" content="address=no" />
      {robots && <meta name="robots" content={robots} />}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:locale" content={lang} />
      <meta property="og:type" content="website" />
      {ogType && <meta property="og:type" content={ogType} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      {getOgImage(seo?.imageOg?.node.resize || ogImage).map((item) => (
        <meta property={item.property} content={item.content} key={item.property} />
      ))}
      {path && (
        <link
          rel="canonical"
          href={`${site.siteMetadata.siteUrl}${withPrefix(normalizePathWithQuery(path))}`}
        />
      )}
      {!!hrefLangs?.length && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={generatedLocale ? `${site.siteMetadata.siteUrl}${generatedLocale}` : defaultLocale}
        />
      )}
      {hrefLangs?.map((item) => (
        <link
          rel="alternate"
          hrefLang={item.language.toLowerCase().replace("_", "-")}
          href={`${site.siteMetadata.siteUrl}${
            item.slug
              ? normalizeJoinPaths(item.prefix, item.slug)
              : normalizeRelativePath(item.prefix)
          }`}
          key={item.language + item.slug}
        />
      ))}
      {children}
      <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
      <script type="text/plain" id="vwoCode" className="optanon-category-C0002">
        {`
          window._vwo_code || (function() {
          var account_id=775267,
          version=2.1,
          settings_tolerance=2000,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},
          addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})()
          ;
       `}
      </script>
    </>
  );
};

Seo.defaultProps = {
  keywords: [],
};

Seo.propTypes = {
  description: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
};

export default Seo;
export const seoFragment = graphql`
  fragment SeoFragment on LiferaySeo {
    titleSeo
    descriptionSeo
    descriptionOg
    typeOg
    keywordSeo
    titleOg
    urlOg
    imageOg {
      node {
        resize(width: 1200, height: 627) {
          src
        }
      }
    }
    fields {
      hrefLanguages
    }
  }
`;
